import 'regenerator-runtime/runtime';
import { AnyAction } from 'redux';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalActions,
  GlobalPut,
  QuoteCheckoutDaten,
  QuoteDbCheckoutPayload,
  QuoteDbInitPayload,
  QuoteDbVorversicherung,
} from '@ps20-frontend/shop-integration-redux';
import { App, areAppTypesEquivalent, convertVermittlernummerToString } from '@ps20-frontend/frontend-models';
import {
  Dialogschritt,
  EinsatzgebieteTier,
  KennzeichnungsArt,
  QuoteTkvDaten,
  QuoteTkvV2Daten,
  QuoteUnfallDaten,
  SpartenType,
  Tierart,
} from '@ps20/bff-models';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  convertDateToDayJs,
  DATEFORMAT_LOCAL_STATE,
  DATEFORMAT_SHORT_ISO,
} from '@ps20-frontend/utils';
import { selectActiveVermittlerNummer } from '@ps20-frontend/apps/checkout-store';
import { Kennzeichnungen } from '../reducer/tkvAngaben.reducer';
import { Produkt } from '../model';
import { IAppState } from '../localStore';
import {
  ANTRAG_EINREICHEN_POLLING_EVENTS,
  CAP_UPLOAD_EVENTS,
} from './pollingHelper/polling-events';

export function* watchQuoteDB() {
  yield takeLatest(
    GlobalActions.QUOTEDB_CHECKOUT_DATA_REQUESTED,
    safeSaga(getQuoteDbData, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    CAP_UPLOAD_EVENTS.UPLOAD_SUCCESSFUL,
    safeSaga(quoteDbInit, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    ANTRAG_EINREICHEN_POLLING_EVENTS.SUCCESSFUL,
    safeSaga(quoteDbInit, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    GlobalActions.VERSICHERUNGSBEGINN_CHANGED,
    safeSaga(quoteDbInit, App.SHOP_CHECKOUT),
  );
  yield takeLatest(
    GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED,
    safeSaga(quoteDbInit, App.SHOP_CHECKOUT),
  );
}

export function* getQuoteDbData() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  let vorversicherungen: Array<QuoteDbVorversicherung> = [];
  const activeVermittlerNummer = selectActiveVermittlerNummer(state.vermittler);

  if (state.vorversicherungen.length > 0) {
    vorversicherungen = state.vorversicherungen
      .filter(arg => arg.vorhanden && arg.vorversicherungsListe[0]?.versicherungsgesellschaft)
      .map(arg => ({
        appId: arg.appId,
        angebotsId: arg.angebotsId,
        gesellschaft: arg.vorversicherungsListe[0].versicherungsgesellschaft,
      }));
  }
  const quoteTkvDaten: Array<QuoteTkvDaten> = [];
  const quoteTkvV2Daten: Array<QuoteTkvV2Daten> = [];
  if (state.tkvAngaben.angabenListe != null) {
    state.tkvAngaben.angabenListe.forEach(angabenListEntry => {
      quoteTkvDaten.push({
        angebotsId: angabenListEntry.angebotsId,
        tiername: angabenListEntry.name,
        tierident: mapKennzeichnungToTierident(angabenListEntry.kennzeichnungsArt),
        einsatzgebiet: angabenListEntry.einsatzgebiet,
        spartenType: SpartenType.TIERKRANKEN
      } as QuoteTkvDaten);
    });
  }
  if (state.tkvAngabenV2.angabenItems != null) {
    state.tkvAngabenV2.angabenItems.forEach(angabenItem => {
      const hundEinsatzgebiet = angabenItem.isAssistanceDog
        ? EinsatzgebieteTier.DIENSTHUND
        : EinsatzgebieteTier.PRIVATHUND;
      const einsatzgebiet = angabenItem.animalType === Tierart.VRHUND ? hundEinsatzgebiet : null;
      quoteTkvV2Daten.push({
        angebotsId: angabenItem.angebotsId,
        tiername: angabenItem.animalName,
        tierident: mapKennzeichnungToTierident(angabenItem.animalIdentificationType),
        einsatzgebiet,
        spartenType: SpartenType.TIERKRANKEN_V2,
      } as QuoteTkvV2Daten);
    });
  }

  let quoteUnfallDaten: QuoteUnfallDaten[] = [];
  if (state.unfAngaben.vpAngabenListe?.length > 0) {
    quoteUnfallDaten = createQuoteUnfallDaten(state);
  }

  const quoteCheckoutDaten: Array<QuoteCheckoutDaten> = state.produkte
    ?.filter(produkt => produkt.sofortschutzActive && produkt.sofortschutzAvailable)
    .map(produkt => ({
      appId: produkt.appId,
      angebotsId: produkt.angebotsId,
      sofortschutzEnde: findSofortschutz(state, produkt),
    }));

  const quoteDbPayload: QuoteDbCheckoutPayload = {
    vtnr: valueOrDefault(
      state.vermittler &&
        state.vermittler.selectedVermittlernummer &&
        convertVermittlernummerToString(activeVermittlerNummer),
      null,
    ),
    vorversicherungen,
    quoteTkvDaten,
    quoteTkvV2Daten,
    quoteUnfallDaten,
    quoteCheckoutDaten,
  };
  yield call(globalPut.put, new GlobalActions().quoteDBDataCheckoutGenerated(quoteDbPayload));
}

export function* quoteDbInit(action: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();

  if (
    action.type === GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED &&
    !areAppTypesEquivalent(action.payload.appId, App.VWG)
  ) {
    yield cancel();
  }

  if (
    action.type === GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED ||
    action.type === GlobalActions.VERSICHERUNGSBEGINN_CHANGED
  ) {
    for (const _produkt of state.produkte) {
      yield take(GlobalActions.PREISE_INS_DASHBOARD_UEBERNEHMEN);
    }
  }

  const quoteDbRequestInitPayload: QuoteDbInitPayload = {
    dialogschritt: mapDialogschritt(action),
  };

  yield call(globalPut.put, new GlobalActions().quoteDBInit(quoteDbRequestInitPayload));
}

function valueOrDefault<T>(value: T, defaultValue: T): T {
  return value || defaultValue;
}

function mapDialogschritt(action: AnyAction): Dialogschritt {
  const actionType = action.type;
  if (actionType === ANTRAG_EINREICHEN_POLLING_EVENTS.SUCCESSFUL) {
    return Dialogschritt.CHECKOUT_ANTRAG_EINREICHEN;
  } else if (actionType === GlobalActions.VORVERSICHERUNG_VORHANDEN_CHANGED) {
    return Dialogschritt.CHECKOUT_AENDERUNG_VORVERSICHERUNG;
  } else if (actionType === CAP_UPLOAD_EVENTS.UPLOAD_SUCCESSFUL) {
    return Dialogschritt.CHECKOUT_CAP_ABSPRUNG;
  }
  return Dialogschritt.CHECKOUT_AENDERUNG_VERSICHERUNGSBEGINN;
}

export function mapKennzeichnungToTierident(kennzeichnung: Kennzeichnungen | KennzeichnungsArt): string {
  const mapping: Record<string, string> = {
    CHIP: 'C',
    LEBENSNUMMER: 'L',
    TAETOWIERUNG: 'T',
    KEINE: 'K',
  };
  return mapping[kennzeichnung as string] || 'K';
}

export function findSofortschutz(state: IAppState, produkt: Produkt): string {
  const vorversicherungen = state.vorversicherungen.filter(
    vorversicherung =>
      vorversicherung.appId === produkt.appId && vorversicherung.angebotsId === produkt.angebotsId,
  );
  if (vorversicherungen.length === 0) {
    return null;
  }
  const resultDate: string[] = vorversicherungen
    .filter(vorversicherung => vorversicherung.vorversicherungsListe.length > 0)
    .map(vorversicherung => {
      return vorversicherung.vorversicherungsListe
        .map(vorversicherungsItem => vorversicherungsItem.ablaufdatum)
        .reduce(
          (ablauf1, ablauf2) => {
            return convertDateToDayJs(ablauf1).isAfter(convertDateToDayJs(ablauf2))
              ? ablauf1
              : ablauf2;
          },
          vorversicherung.vorversicherungsListe[0].ablaufdatum,
        );
    });
  if (resultDate.length === 0) {
    return null;
  }
  return dayjs(resultDate[0], DATEFORMAT_LOCAL_STATE).format(DATEFORMAT_SHORT_ISO);
}

function createQuoteUnfallDaten(state: IAppState): QuoteUnfallDaten[] {
  return state.unfAngaben.vpAngabenListe.map(vpAngaben => ({
    angebotsId: vpAngaben.angebotsId,
    dynamik: vpAngaben.dynamikSelected,
    bezugsrecht: vpAngaben.bezugsrecht,
    abweichungVomZielmarkt: verkaufImZielmarkt(state, vpAngaben.angebotsId),
    spartenType: SpartenType.UNFALL,
  }));
}

function verkaufImZielmarkt(state: IAppState, angebotsId: AngebotsId['angebotsId']): boolean {
  const produkt = state.produkte.find(produkt => produkt.angebotsId === angebotsId);
  if (produkt) {
    return produkt.abweichungVomZielmarkt;
  }
  return false;
}
